// A aba do grafo: barra de controles, fichas de filtro, canvas e os painéis.
//
// O desenho e a física estão em nucleo.ts/desenho.ts; aqui fica só o que é
// interface. A divisão importa porque foi ela que permitiu portar 730 linhas de
// canvas sem reescrever a matemática — e é o que deixa os filtros testáveis sem
// abrir um navegador.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  KIND, KIND_LABEL, REL_COLOR, REL_LABEL, comCobertura, contarPorRelacao,
  contarPorTipo, donoDe, graus, tiposDoGrafo, type GrafoBruto, type Modo,
} from './nucleo';
import { useGrafo } from './useGrafo';
import { Detalhe } from './Detalhe';
import { Lateral } from './Lateral';
import { VazioDoGrafo } from './Vazio';
import {
  chaves, useContagemDeNotas, useDecisoes, useDocs, useDonos, useGrafoDaMemoria, useGrafoDoCodigo,
  type Decisao,
} from '../api/consultas';
import { pegar, postar } from '../api/cliente';
import { Carregando, Falha } from '../componentes/Estado';
import { copiar, useRecado } from '../componentes/Recado';

const MODOS: { id: Modo; rotulo: string; titulo: string }[] = [
  { id: 'codigo', rotulo: 'código', titulo: 'arquivos, rotas e imports (AST)' },
  { id: 'memoria', rotulo: 'memória', titulo: 'decisões, notas, sessões, lacunas e donos' },
  { id: 'cobertura', rotulo: 'cobertura', titulo: 'o que o time documentou e o que ninguém escreveu' },
];

export function Grafo({ slug }: { slug: string }) {
  const avisar = useRecado();
  const navegar = useNavigate();
  const fila = useQueryClient();
  const [parametros, setParametros] = useSearchParams();
  const [modo, setModo] = useState<Modo>('codigo');
  const [painelAberto, setPainelAberto] = useState(false);

  const codigo = useGrafoDoCodigo(slug);
  // memória entra em cena nos dois modos que a usam; cobertura precisa dela
  // para saber quantos artefatos tocam cada arquivo
  const memoria = useGrafoDaMemoria(slug, modo !== 'codigo');
  const notas = useContagemDeNotas(slug);
  const docs = useDocs(slug);
  const decisoes = useDecisoes(slug);
  const donos = useDonos(slug);

  const notasPorArquivo = notas.data || {};

  const grafo: GrafoBruto | null = useMemo(() => {
    if (modo === 'memoria') return memoria.data ? { nodes: memoria.data.nodes, edges: memoria.data.edges } : null;
    if (modo === 'cobertura') {
      if (!codigo.data) return null;
      const porArquivo = new Map((memoria.data?.coverage || []).map((c) => [c.unit_key, c.memory]));
      return comCobertura(codigo.data, porArquivo);
    }
    return codigo.data ?? null;
  }, [modo, codigo.data, memoria.data]);

  const g = useGrafo(grafo, modo, notasPorArquivo, avisar);
  // grau sobre o grafo inteiro, e não sobre o filtrado: é o que a dica do painel
  // atual mostra, e o número não pode encolher porque alguém desligou um tipo
  const grausDoNo = useMemo(() => graus(grafo?.edges || []), [grafo]);

  const decisoesPorUnidade = useMemo(() => {
    const m = new Map<string, Decisao[]>();
    for (const d of decisoes.data || []) {
      for (const l of d.links || []) {
        if (!m.has(l)) m.set(l, []);
        m.get(l)!.push(d);
      }
    }
    return m;
  }, [decisoes.data]);

  // nó de arquivo tem node_key === file: sem dedupe a mesma decisão aparece duas vezes
  const decisoesDe = useCallback((chave: string) => {
    const no = g.selecionado;
    const juntas = [...(decisoesPorUnidade.get(chave) || [])];
    if (no?.file && no.file !== chave) juntas.push(...(decisoesPorUnidade.get(no.file) || []));
    return [...new Map(juntas.map((d) => [d.unit_key, d])).values()];
  }, [decisoesPorUnidade, g.selecionado]);

  const { ajustarTela, selecionar, setBusca, busca } = g;

  // a busca do nó entra na URL: "o grafo mostrando X" é link de revisão de
  // código, e era o que o painel atual não conseguia compartilhar
  const semeado = useRef(false);
  useEffect(() => {
    if (semeado.current) return;
    semeado.current = true;
    const inicial = parametros.get('no');
    if (inicial) setBusca(inicial);
  }, [parametros, setBusca]);
  useEffect(() => {
    if (!semeado.current) return;
    if ((parametros.get('no') || '') === busca) return;
    const p = new URLSearchParams(parametros);
    if (busca) p.set('no', busca); else p.delete('no');
    setParametros(p, { replace: true });
  }, [busca, parametros, setParametros]);

  // F, Esc e P valem com o foco em qualquer lugar da aba, como no painel atual;
  // seta e zoom continuam só com o canvas focado, senão a página inteira andaria
  useEffect(() => {
    const naTecla = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement | null;
      if (alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA')) return;
      if (e.key === 'f' || e.key === 'F') ajustarTela();
      if (e.key === 'Escape') { selecionar(null); setBusca(''); }
      if (e.key === 'p' || e.key === 'P') { e.preventDefault(); setPainelAberto((v) => !v); }
    };
    document.addEventListener('keydown', naTecla);
    return () => document.removeEventListener('keydown', naTecla);
  }, [ajustarTela, selecionar, setBusca]);

  /**
   * Troca de modo espera o grafo da memória chegar antes de trocar.
   *
   * É o que o painel atual faz (graph.js:96, um `await` antes do applyGraph), e
   * o motivo é a tela: sem isso o modo muda primeiro, a consulta fica pendente e
   * a aba inteira pisca num "carregando" — perdendo a barra de controles junto.
   * Se a busca falhar, o modo não muda e o recado explica; era assim antes.
   */
  const [trocandoModo, setTrocandoModo] = useState(false);
  const escolherModo = useCallback(async (novo: Modo) => {
    if (novo === modo || trocandoModo) return;
    if (novo !== 'codigo' && !memoria.data) {
      setTrocandoModo(true);
      try {
        await fila.ensureQueryData({
          queryKey: chaves.memoria(slug),
          queryFn: () => pegar<GrafoBruto>(`/api/${slug}/memory-graph`),
        });
      } catch {
        avisar('não deu para carregar o grafo da memória');
        return;
      } finally {
        setTrocandoModo(false);
      }
    }
    setModo(novo);
    selecionar(null);
  }, [modo, trocandoModo, memoria.data, fila, slug, avisar, selecionar]);

  const escrever = useMutation({
    mutationFn: async ({ rota, corpo }: { rota: string; corpo: unknown }) => postar(rota, corpo),
    onSuccess: async () => {
      // a memória mudou: a cobertura tem de ser relida, senão o nó continua apagado
      await fila.invalidateQueries({ queryKey: chaves.memoria(slug) });
      await fila.invalidateQueries({ queryKey: chaves.notas(slug) });
    },
  });

  const acoes = {
    abrirDoc: (chave: string) => navegar(`/p/${slug}/doc?key=${encodeURIComponent(chave)}`),
    abrirImpacto: (chave: string) => navegar(`/p/${slug}/impact?key=${encodeURIComponent(chave)}`),
    abrirSessao: (id: string) => navegar(`/p/${slug}/sessions?id=${encodeURIComponent(id)}`),
    irParaAba: (aba: string) => navegar(`/p/${slug}/${aba}`),
    copiarComando: async (chave: string) => {
      avisar(await copiar(`eng doc ${chave}`) ? 'comando copiado' : 'não deu para copiar');
    },
    focarNo: g.focarNo,
    isolar: g.isolarVizinhanca,
    verTudo: g.verTudo,
    fechar: () => g.selecionar(null),
  };

  if (codigo.isPending) return <Carregando rotulo="o grafo" />;
  if (codigo.error) {
    return <Falha erro={codigo.error} rotulo="o grafo do código" aoRepetir={codigo.refetch} />;
  }
  // o modo só muda com a memória já em mãos, então isto é rede de segurança
  // para quem chegar aqui com a consulta invalidada no meio do caminho
  if (modo !== 'codigo' && !memoria.data) return <Carregando rotulo="o grafo da memória" />;

  const contagemTipos = contarPorTipo(grafo);
  const contagemRel = contarPorRelacao(grafo);
  const totalNos = (grafo?.nodes || []).length;
  const totalArestas = (grafo?.edges || []).length;
  const semMemoria = modo === 'cobertura'
    ? (grafo?.nodes || []).filter((n) => n.kind === 'file' && !n.memory).length
    : 0;

  return (
    <>
      <div className="g-bar">
        <div className="g-find">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="m20 20-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <label className="sr-only" htmlFor="g-q">achar nó no grafo</label>
          <input
            id="g-q" value={g.busca}
            placeholder="achar nó: DispatchService · /v1/messages · dispatch.service.ts"
            onChange={(e) => g.setBusca(e.target.value.trim())}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              if (!g.achados.length) return avisar('nenhum nó com esse nome');
              if (g.achados.length === 1) g.focarNo(g.achados[0].node_key);
              else g.ajustarTela();
            }}
          />
          <span id="g-q-n" className="muted">{g.busca ? `${g.achados.length} nó(s)` : ''}</span>
        </div>

        <div className="seg" role="group" aria-label="leitura do grafo">
          {MODOS.map((m) => (
            <button key={m.id} type="button" title={m.titulo}
              className={modo === m.id ? 'on' : undefined}
              aria-pressed={modo === m.id}
              disabled={trocandoModo}
              onClick={() => { void escolherModo(m.id); }}>
              {m.rotulo}
            </button>
          ))}
        </div>

        <div className="seg" role="group" aria-label="layout do grafo">
          <button type="button" title="endpoint → controller → service → arquivo"
            className={g.layout === 'layers' ? 'on' : undefined}
            aria-pressed={g.layout === 'layers'}
            onClick={() => g.setLayout('layers')}>camadas</button>
          <button type="button" title="layout por força"
            className={g.layout === 'force' ? 'on' : undefined}
            aria-pressed={g.layout === 'force'}
            onClick={() => g.setLayout('force')}>força</button>
        </div>

        <label className="sr-only" htmlFor="g-depth">vizinhança do nó selecionado</label>
        <select id="g-depth" title="quanto mostrar ao redor do nó selecionado"
          value={String(g.filtros.profundidade)}
          onChange={(e) => g.setProfundidade(Number(e.target.value))}>
          <option value="0">vizinhança: tudo</option>
          <option value="1">só 1 salto</option>
          <option value="2">só 2 saltos</option>
        </select>

        <label className="chk">
          <input type="checkbox" checked={g.rotulos} onChange={(e) => g.setRotulos(e.target.checked)} /> rótulos
        </label>
        <label className="chk">
          <input type="checkbox" checked={g.filtros.esconderIsolados}
            onChange={(e) => g.setEsconderIsolados(e.target.checked)} /> esconder isolados
        </label>

        <button className="btn ghost sm" type="button" title="ajustar à tela (F)" onClick={g.ajustarTela}>ajustar</button>
        <button className="btn ghost sm" type="button" title="reorganizar o layout" onClick={g.reorganizar}>reorganizar</button>
        <button className="btn ghost sm" type="button" title="rotas, hubs e órfãos"
          aria-pressed={painelAberto} onClick={() => setPainelAberto((v) => !v)}>painel</button>
        {g.recortado && (
          <button className="btn ghost sm" type="button"
            title="devolve todos os tipos, relações e a vizinhança inteira"
            onClick={g.limparFiltros}>limpar filtros</button>
        )}
      </div>

      <div className="g-chips">
        <div className="g-filters">
          {tiposDoGrafo(grafo).map((k) => (
            <button key={k} type="button"
              className={`g-filter${g.filtros.tipos.has(k) ? '' : ' off'}`}
              style={{ borderColor: `var(${KIND[k] || '--line-strong'})` }}
              aria-pressed={g.filtros.tipos.has(k)}
              onClick={() => g.alternarTipo(k)}>
              <i className="kdot" style={{
                background: `var(${KIND[k] || '--ink-faint'})`,
                width: 8, height: 8, display: 'inline-block', marginRight: 5, verticalAlign: 'middle',
              }} />
              {KIND_LABEL[k] || k}<span className="c">{contagemTipos[k]}</span>
            </button>
          ))}
        </div>
        <div className="g-sep" />
        <div className="g-filters">
          {Object.keys(contagemRel).sort().map((r) => (
            <button key={r} type="button"
              className={`g-filter rel${g.filtros.relacoes.has(r) ? '' : ' off'}`}
              style={{ borderColor: `var(${REL_COLOR[r] || '--line-strong'})` }}
              aria-pressed={g.filtros.relacoes.has(r)}
              onClick={() => g.alternarRelacao(r)}>
              <i style={{
                display: 'inline-block', width: 14,
                borderTop: `2px solid var(${REL_COLOR[r] || '--ink-faint'})`,
                marginRight: 5, verticalAlign: 'middle',
              }} />
              {REL_LABEL[r] || r}<span className="c">{contagemRel[r]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="graph-wrap" ref={g.aoMontarMoldura}>
        <canvas
          ref={g.aoMontarCanvas} tabIndex={0} role="img" id="graph"
          aria-label="mapa do código: setas arrastam, mais e menos aproximam, P abre o painel com a mesma informação em texto"
          onMouseMove={g.eventos.onMouseMove}
          onMouseDown={g.eventos.onMouseDown}
          onDoubleClick={g.eventos.onDoubleClick}
          onMouseLeave={g.eventos.onMouseLeave}
          onKeyDown={g.eventos.onKeyDown}
        />
        {g.dica && (
          <div className="gtip" style={{
            display: 'block',
            left: Math.min(g.dica.x + 16, Math.max(8, (g.refMoldura.current?.clientWidth || 0) - 300)),
            top: g.dica.y + 14,
          }}>
            <b>{String(g.dica.no.label || g.dica.no.node_key).replace(/^pkg:/, '')}</b><br />
            {KIND_LABEL[g.dica.no.kind] || g.dica.no.kind}
            {(() => {
              const n = g.dica!.no;
              const extra = n.http ? `${n.http.method} ${n.http.path}` : (n.signature || n.author || n.file || '');
              return extra ? <> · <i>{String(extra).slice(0, 60)}</i></> : null;
            })()}
            <br />
            <i>
              {(() => {
                const n = g.dica!.no;
                if (modo === 'cobertura') {
                  return n.memory
                    ? `${n.memory} artefato(s) de memória`
                    : 'ninguém escreveu sobre este arquivo';
                }
                const notas = n.file ? (notasPorArquivo[n.file] || 0) : 0;
                const rel = `${grausDoNo.get(n.node_key) || 0} relações`;
                return notas ? `${rel} · ${notas} nota${notas > 1 ? 's' : ''}` : rel;
              })()}
              {g.dica.no.review_state ? ` · ${g.dica.no.review_state}` : ''}
              {g.dica.no.wt ? ' · não commitado' : ''}
            </i>
          </div>
        )}

        {painelAberto && (
          <Lateral
            grafo={grafo} memoria={memoria.data ?? null} modo={modo}
            aoEscolher={g.focarNo} aoFechar={() => setPainelAberto(false)}
          />
        )}

        {g.selecionado && (
          <Detalhe
            no={g.selecionado}
            grafo={grafo}
            modo={modo}
            profundidade={g.filtros.profundidade}
            notasPorArquivo={notasPorArquivo}
            temDoc={(chave) => (docs.data || []).some((d) => d.unit_key === chave)}
            dono={(chave) => donoDe(donos.data || [], chave)}
            decisoesDe={decisoesDe}
            acoes={acoes}
            aoEscreverNota={async (chave, texto) => {
              if (!texto) return avisar('escreva a nota primeiro');
              await escrever.mutateAsync({
                rota: `/api/${slug}/notes`,
                corpo: { unit_key: chave, body_md: texto, kind: 'detalhe', anchor_kind: 'doc' },
              }).then(
                () => avisar('nota registrada — já entra no contexto do agente'),
                (e: Error) => avisar(e.message),
              );
            }}
            aoAbrirLacuna={async (chave, texto) => {
              await escrever.mutateAsync({
                rota: `/api/${slug}/gaps`,
                corpo: { kind: 'sem_doc', unit_key: chave, detail: texto || `ninguém documentou ${chave}` },
              }).then(
                () => avisar('lacuna aberta — aparece na saúde e para os agentes'),
                (e: Error) => avisar(e.message),
              );
            }}
          />
        )}

        <div className="g-legend">{legenda(modo)}</div>

        {!g.visiveis.nos && (
          <VazioDoGrafo
            grafo={grafo} codigo={codigo.data ?? null} modo={modo} slug={slug}
            aoLimparFiltros={g.limparFiltros}
            aoVerCodigo={() => { void escolherModo('codigo'); }}
          />
        )}
      </div>

      <div className="g-status">
        <span>
          <b>{g.visiveis.nos}</b>/{totalNos} nós · <b>{g.visiveis.arestas}</b>/{totalArestas} relações
        </span>
        {modo === 'cobertura'
          ? <span><b>{semMemoria}</b> arquivo(s) sem memória</span>
          : !totalNos
            ? <span>nada para desenhar</span>
            : <span>{g.visiveis.isolados ? <><b>{g.visiveis.isolados}</b> sem relação</> : 'todos conectados'}</span>}
        <span>zoom {(g.zoom * 100).toFixed(0)}%</span>
        {g.recortado && <span className="warn-inline">recortado por filtro</span>}
        <span className="g-keys">
          roda <span className="kbd">zoom</span> · arrastar fundo move · clique abre detalhe · duplo clique solta o nó
          {' · '}<span className="kbd">F</span> ajusta · <span className="kbd">P</span> painel
          {' · '}<span className="kbd">Esc</span> limpa
        </span>
      </div>
    </>
  );
}

function legenda(modo: Modo) {
  if (modo === 'memoria') {
    return (
      <>
        <div><i style={{ borderColor: 'var(--violet)' }} />decisão governa arquivo</div>
        <div><i style={{ borderColor: 'var(--brand-2)' }} />nota anota arquivo</div>
        <div><i style={{ borderColor: 'var(--bad)', borderTopStyle: 'dotted' }} />deriva: cita código que sumiu</div>
        <div style={{ marginTop: 3 }}>este grafo é o que o time <b>escreveu</b>, não o que o código faz</div>
      </>
    );
  }
  if (modo === 'cobertura') {
    return (
      <>
        <div><i style={{ borderColor: 'var(--ok)' }} />tem decisão, nota ou sessão</div>
        <div><i style={{ borderColor: 'var(--ink-faint)', borderTopStyle: 'dashed' }} />ninguém escreveu nada</div>
        <div style={{ marginTop: 3 }}>clique no apagado para escrever nota ou abrir lacuna</div>
      </>
    );
  }
  return (
    <>
      <div><i style={{ borderColor: 'var(--ok)' }} />extraído do AST</div>
      <div><i style={{ borderColor: 'var(--warn)', borderTopStyle: 'dashed' }} />inferido</div>
      <div><i style={{ borderColor: 'var(--bad)', borderTopStyle: 'dotted' }} />ambíguo</div>
      <div style={{ marginTop: 3 }}>seta = direção da dependência</div>
    </>
  );
}
